import React from "react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { Marquee } from "../components/Marquee";
import { MANIFESTO, STATS, IMAGES } from "../data/content";

const VALUES = [
  { t: "Athletics-first", d: "We don't retrofit business software onto sports. Every schema is built for athletes, events, and seasons." },
  { t: "Radically fast", d: "Millisecond search and real-time dashboards, because a tool that's slow is a tool that goes unused." },
  { t: "Partner, not vendor", d: "We stay with your program season after season, evolving the platform as your needs grow." },
];

export default function About() {
  return (
    <div>
      <PageHero
        testid="about-hero"
        overline="About"
        title={<>The team behind<br />the data</>}
        subtitle="Athletic Data Solutions builds the software layer for modern athletics — trusted by associations, coaches, and directors to make sense of the numbers."
      />

      <section className="py-20 bg-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-teal/30" />
              <img src={IMAGES.training} alt="Athletes training" className="relative w-full aspect-square object-cover grayscale-[0.2]" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-5">Who we are</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight mb-6">
                Engineers and athletes obsessed with structured data.
              </h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                We started Athletic Data Solutions after watching programs bury decades of history in
                filing cabinets and spreadsheets. Records went unverified, training data went unused, and
                eligibility was tracked on paper.
              </p>
              <p className="text-slate-400 leading-relaxed">
                So we built the platforms we wished existed — fast, searchable, and intuitive enough for
                anyone on the field. Today we serve programs from single schools to statewide associations.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-dark border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-orange mb-16">What we believe</p>
          </Reveal>
          <div className="space-y-px">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-white/15 group">
                  <div className="md:col-span-2">
                    <span className="font-display text-6xl font-black text-transparent" style={{ WebkitTextStroke: "1.5px rgba(255,74,28,0.4)" }}>{m.n}</span>
                  </div>
                  <h3 className="md:col-span-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight group-hover:text-orange transition-colors duration-300">{m.title}</h3>
                  <p className="md:col-span-4 text-slate-400 leading-relaxed self-center">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee text="STRUCTURE · SPEED · CLARITY" separator="·" />

      <section className="py-24 bg-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.08}>
              <div className="bg-dark p-10 h-full">
                <h3 className="font-display text-2xl font-bold mb-3 text-teal">{v.t}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14">
          <CTAButton to="/contact" variant="orange" data-testid="about-cta">Work with us</CTAButton>
        </div>
      </section>
    </div>
  );
}
